import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useMemo, useState } from "react";
import { toast } from "sonner";

import { listUsers } from "@/api/admin";
import { searchStructuralUnits } from "@/api/one_c_pickers";
import {
  type ReceiverBinding,
  type WarehouseRef,
  listReceiverBindings,
  setReceiverBinding,
} from "@/api/transfer_receipts";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { extractErrorMessage, toastError } from "@/lib/api-errors";
import { LoadState } from "@/lib/scan-ui/screen";
import { OrgWhitelistCard } from "@/pages/settings/cards/OrgWhitelistCard";
import { SettingsPageHeader } from "@/pages/settings/SettingsPageHeader";
import { useSystemSettingsQuery } from "@/pages/settings/useSystemSettingsQuery";

const SUBTITLE =
  "Перемещения на склады из списка — в два шага: отправитель отправляет, получатель принимает.";

// Пустой список — функция ВЫКЛЮЧЕНА (все перемещения — как раньше), а не «все
// склады»: подпись обязана говорить то же, что делает бэкенд
// (`two_step.requires_receipt_for`, Р4: получатель ИЛИ отправитель в списке).
const WAREHOUSES_HINT =
  "Приёмка нужна, когда в списке склад-получатель или склад-отправитель: всё, что через портал едет в склад " +
  "из списка и из него. Принимает тот, кто привязан к складу-получателю. Склад должен быть и в складах " +
  "раздела «Перемещение» (для перемещений без заказа). Ничего не отмечено — приёмка выключена.";

const WAREHOUSES_QUERY_KEY = "transfer-receipt-warehouses-all";

// Бэкенд отдаёт поиск справочника не больше 50 строк (`$top=50`): список ровно
// на потолке — скорее всего обрезан, и склада в нём может не быть.
const WAREHOUSES_CAP = 50;
const WAREHOUSES_CAPPED_TEXT =
  "Список складов из 1С обрезан на 50 — поиск может не найти склад; сообщите разработчику";
const NOT_IN_1C = " — нет в 1С";

/** Настройки «Приёмки перемещений»: склады с подтверждением и принимающие. */
export function TransferReceiptSettingsPage(): React.ReactElement {
  const { data, isLoading, error, refetch } = useSystemSettingsQuery();

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <SettingsPageHeader title="Приёмка перемещений" subtitle={SUBTITLE} />
      {isLoading || error || !data ? (
        <LoadState
          isLoading={isLoading}
          error={error}
          onRetry={() => void refetch()}
          className="flex flex-col items-center gap-3 p-4 text-center"
        />
      ) : (
        <>
          <OrgWhitelistCard
            title="Склады с подтверждением приёмки"
            field="transfer_receipt_warehouse_refs"
            current={data.transfer_receipt_warehouse_refs}
            searchFn={(q) => searchStructuralUnits("Склад", q)}
            queryKey={WAREHOUSES_QUERY_KEY}
            entityLabel="склады"
            hint={WAREHOUSES_HINT}
            emptyLabel="выключено"
          />
          <ReceiverBindingsCard receiptRefs={data.transfer_receipt_warehouse_refs} />
        </>
      )}
    </div>
  );
}

/**
 * Принимающие: пользователь → склады, на которые он принимает. Не-админ видит
 * во «Входящих» только перемещения на свои склады; без привязки — отказ
 * «попросите администратора». Права `transfer_receive` выдаются отдельно, в
 * «Пользователях». Выбор — из ВСЕХ складов 1С (Р4: из склада списка едут
 * возвраты на склад производства, а его принимают кладовщики, привязанные к
 * складу вне списка); сверху — склады из списка (`receiptRefs`, сохранённое
 * значение). Уже привязанный склад, которого в 1С нет, остаётся в редакторе —
 * чтобы его можно было снять.
 */
function ReceiverBindingsCard({ receiptRefs }: { receiptRefs: string[] }): React.ReactElement {
  const qc = useQueryClient();
  const bindingsQ = useQuery({ queryKey: ["transfer-receipt-bindings"], queryFn: listReceiverBindings });
  const usersQ = useQuery({ queryKey: ["admin", "users"], queryFn: listUsers });
  const whQ = useQuery({
    queryKey: [WAREHOUSES_QUERY_KEY],
    queryFn: () => searchStructuralUnits("Склад", ""),
    staleTime: 5 * 60_000,
  });
  const [editing, setEditing] = useState<{ userId: number; username: string; refs: Set<string> } | null>(null);
  const [pickUser, setPickUser] = useState("");
  const [q, setQ] = useState("");

  const saveM = useMutation({
    mutationFn: ({ userId, warehouses }: { userId: number; warehouses: WarehouseRef[] }) =>
      setReceiverBinding(userId, warehouses),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["transfer-receipt-bindings"] });
      toast.success("Сохранено");
      setEditing(null);
    },
    onError: toastError("Ошибка сохранения"),
  });

  const bindings = bindingsQ.data ?? [];
  // Имя склада — из 1С; у склада, которого в выборке уже нет (удалён), —
  // из самой привязки, чтобы снять его можно было по имени, а не по GUID.
  const names = useMemo(() => {
    const m = new Map<string, string>();
    for (const b of bindings) for (const w of b.warehouses) m.set(w.ref, w.name);
    for (const w of whQ.data ?? []) m.set(w.ref, w.name);
    return m;
  }, [bindings, whQ.data]);
  // Склады, которые 1С вернула сейчас: привязанный склад вне набора — удалён/скрыт в 1С.
  const inOneC = useMemo(() => new Set((whQ.data ?? []).map((w) => w.ref)), [whQ.data]);
  const oneCLoaded = whQ.isSuccess;
  const capped = (whQ.data?.length ?? 0) >= WAREHOUSES_CAP;
  const bound = new Set(bindings.map((b) => b.user_id));
  const candidates = (usersQ.data ?? []).filter((u) => u.is_active && !bound.has(u.id));

  function startEdit(b: Pick<ReceiverBinding, "user_id" | "username" | "warehouses">) {
    setQ("");
    setEditing({ userId: b.user_id, username: b.username, refs: new Set(b.warehouses.map((w) => w.ref)) });
  }
  function toggle(ref: string) {
    setEditing((e) => {
      if (!e) return e;
      const refs = new Set(e.refs);
      if (refs.has(ref)) refs.delete(ref);
      else refs.add(ref);
      return { ...e, refs };
    });
  }
  function save() {
    if (!editing) return;
    const missing = [...editing.refs].filter((ref) => !names.has(ref));
    if (missing.length) {
      // Имя склада уходит в привязку — сохранять GUID без имени нельзя.
      toast.error(`Не найдено имя склада ${missing.join(", ")} — перезагрузите страницу`);
      return;
    }
    const warehouses = [...editing.refs].map((ref) => ({ ref, name: names.get(ref)! }));
    saveM.mutate({ userId: editing.userId, warehouses });
  }

  const receipt = new Set(receiptRefs);
  // Все склады 1С и уже привязанные, которых в 1С нет (их — снять), по
  // алфавиту; склады из списка приёмки — первой группой.
  const whRows = editing
    ? [...new Set([...(whQ.data ?? []).map((w) => w.ref), ...editing.refs])]
        .map((ref) => ({ ref, name: names.get(ref) ?? ref, gone: !inOneC.has(ref) }))
        .filter((w) => !q || w.name.toLowerCase().includes(q.toLowerCase()))
        .sort((a, b) => a.name.localeCompare(b.name, "ru"))
    : [];
  const listedRows = whRows.filter((w) => receipt.has(w.ref));
  const otherRows = whRows.filter((w) => !receipt.has(w.ref));
  const renderRow = (w: { ref: string; name: string; gone: boolean }) => (
    <label key={w.ref} className="flex items-center gap-2 text-sm">
      <input
        type="checkbox"
        checked={editing?.refs.has(w.ref) ?? false}
        onChange={() => toggle(w.ref)}
        className="size-4"
      />
      {w.name}
      {w.gone && <span className="text-xs text-destructive">{NOT_IN_1C}</span>}
    </label>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Принимающие</CardTitle>
        <p className="text-sm text-muted-foreground">
          Кто на какие склады принимает: технолог — на склад из списка, кладовщики — на склад, куда приходят
          возвраты из него. Получатель видит и принимает только перемещения на свои склады; право «Приёмка
          перемещений» выдаётся в «Пользователях».
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {bindingsQ.isLoading && <p className="text-sm text-muted-foreground">Загрузка…</p>}
        {bindingsQ.isError && (
          <p className="text-sm text-destructive">
            Не удалось загрузить привязки: {extractErrorMessage(bindingsQ.error)}
          </p>
        )}
        {!bindingsQ.isLoading && !bindingsQ.isError && bindings.length === 0 && (
          <p className="text-sm text-muted-foreground">Пока никто не привязан.</p>
        )}
        {bindings.map((b) => (
          <div key={b.user_id} data-testid="binding-row" className="rounded border p-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="font-medium">{b.full_name || b.username}</div>
                {b.full_name && <div className="text-xs text-muted-foreground">{b.username}</div>}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => startEdit(b)}>
                  Изменить
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={saveM.isPending}
                  onClick={() => saveM.mutate({ userId: b.user_id, warehouses: [] })}
                >
                  Снять все
                </Button>
              </div>
            </div>
            <div className="mt-1 flex flex-wrap gap-1">
              {b.warehouses.map((w) => (
                <span key={w.ref} className="rounded bg-muted px-1.5 py-0.5 text-xs">
                  {w.name}
                  {oneCLoaded && !inOneC.has(w.ref) && <span className="text-destructive">{NOT_IN_1C}</span>}
                </span>
              ))}
            </div>
          </div>
        ))}

        {!editing && (
          <div className="flex flex-wrap items-center gap-2">
            <select
              aria-label="Пользователь"
              value={pickUser}
              onChange={(e) => setPickUser(e.target.value)}
              className="h-9 min-w-0 flex-1 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="">
                {usersQ.isLoading ? "Загрузка пользователей…" : "Выберите пользователя"}
              </option>
              {candidates.map((u) => (
                <option key={u.id} value={String(u.id)}>
                  {u.username}
                </option>
              ))}
            </select>
            <Button
              disabled={!pickUser}
              onClick={() => {
                const u = candidates.find((x) => String(x.id) === pickUser);
                if (!u) return;
                setPickUser("");
                startEdit({ user_id: u.id, username: u.username, warehouses: [] });
              }}
            >
              Привязать
            </Button>
          </div>
        )}
        {usersQ.isError && (
          <p className="text-sm text-destructive">
            Не удалось загрузить пользователей: {extractErrorMessage(usersQ.error)}
          </p>
        )}

        {editing && (
          <div className="space-y-2 rounded border border-primary/40 p-2" data-testid="binding-editor">
            <div className="text-sm font-medium">Склады пользователя {editing.username}</div>
            <Input placeholder="Поиск (склады)" value={q} onChange={(e) => setQ(e.target.value)} />
            {whQ.isLoading && <p className="text-sm text-muted-foreground">Загрузка складов…</p>}
            {whQ.isError && (
              <p className="text-sm text-destructive">
                Не удалось загрузить склады из 1С: {extractErrorMessage(whQ.error)}
              </p>
            )}
            {capped && <p className="text-sm text-destructive">{WAREHOUSES_CAPPED_TEXT}</p>}
            {!whQ.isLoading && !whQ.isError && (
              <div className="max-h-72 space-y-1 overflow-auto rounded border p-2">
                {listedRows.length > 0 && (
                  <>
                    <div className="text-xs text-muted-foreground">Склады с приёмкой</div>
                    {listedRows.map(renderRow)}
                  </>
                )}
                {listedRows.length > 0 && otherRows.length > 0 && (
                  <div className="pt-2 text-xs text-muted-foreground">
                    Остальные — принимают перемещения со складов с приёмкой (возвраты)
                  </div>
                )}
                {otherRows.map(renderRow)}
                {whRows.length === 0 && <p className="text-sm text-muted-foreground">Ничего не найдено</p>}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={save} disabled={saveM.isPending}>
                Сохранить
              </Button>
              <Button variant="ghost" onClick={() => setEditing(null)}>
                Отмена
              </Button>
              <span className="text-xs text-muted-foreground">Выбрано: {editing.refs.size}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
